import { useState } from "react";
import { ArrowRight, Mic } from "lucide-react";
import type { HubAskBoxProps } from "@/types/help-hub/presentation";
import { hubBrandTheme, inkOn } from "../brandInk";

/**
 * The hero's ask box — the page's primary action.
 *
 * v1 put "Ask the Genie" behind a tile you had to identify and click before
 * anything happened. A visitor arriving with a question in their head should be
 * able to type it into the first thing they see, which is what this is. The
 * voice button sits beside it rather than under a second tile, because "talk"
 * and "type" are the same door.
 */
export const HubAskBox = ({ placeholder, branding, onAsk, onTalk }: HubAskBoxProps) => {
  const theme = hubBrandTheme(branding);
  const [value, setValue] = useState("");

  const submit = () => {
    const question = value.trim();
    if (!question) return;
    setValue("");
    onAsk(question);
  };

  return (
    <div className="mx-auto w-full max-w-2xl">
      {/* The signature gradient-border frame, painted in the customer's colours. */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        className="rounded-2xl p-[2px] shadow-lg shadow-black/[0.07]"
        style={{ background: theme.gradient }}
      >
        <div className="flex items-center gap-2 rounded-[14px] bg-white p-2">
          <input
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={placeholder}
            aria-label="Ask a question"
            className="min-w-0 flex-1 rounded-xl bg-transparent px-3 py-3 text-base text-genie-text-primary outline-none placeholder:text-genie-text-muted sm:text-[15px]"
          />
          <button
            type="submit"
            disabled={!value.trim()}
            aria-label="Ask"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-opacity disabled:opacity-40"
            style={{ backgroundColor: theme.primary, color: inkOn(theme.primary) }}
          >
            <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
          </button>
        </div>
      </form>

      <div className="mt-3 flex items-center justify-center">
        <button
          type="button"
          onClick={onTalk}
          className="inline-flex min-h-11 items-center gap-2 rounded-full border border-genie-border bg-white px-4 py-2 text-[13.5px] font-medium text-genie-text-primary transition-shadow hover:shadow-md"
        >
          <Mic className="h-4 w-4" strokeWidth={2.25} />
          Or just talk to us
        </button>
      </div>
    </div>
  );
};

export default HubAskBox;
